import React, { useState, useCallback, useMemo } from 'react';
import { Input, Space, Button, Avatar, Typography, message, Dropdown, Modal, Grid, Flex } from 'antd';
import { SearchOutlined, UserOutlined, PhoneOutlined, MailOutlined, MoreOutlined, EditOutlined, DeleteOutlined, MessageOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ContactsServices from '../../../Contacts/ContactsServices';
import ContactEditModal from '../../../Shared/ContactEditModal/ContactEditModal';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import SharedFilterBar from '../../../Shared/Filters/SharedFilterBar';
import { contactFilterFields, contactSortFields } from '../../../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../../../Shared/Filters/useFilterOptionFields';
import AppTable from '../../../Shared/AppTable/AppTable';
import useContactsFetching from './useContactsFetching';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const CONTACT_FILTER_OPTION_RESOURCES = {
    agent_id: FILTER_OPTION_RESOURCE.AGENT,
};
const customContactFilterFields = contactFilterFields?.filter(field => field.key !== 'channel_id')
/**
 * ContactsTab component displays a paginated table of contacts for a channel.
 * Uses SharedFilterBar for filtering and AppTable for responsive display.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.channel - The channel object containing channel details.
 * @returns {JSX.Element} The rendered ContactsTab component.
 */
const ContactsTab = ({ channel }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const screens = useBreakpoint() || {};
    const { handleError } = useErrorHandler();
    const filterFields = useFilterOptionFields(customContactFilterFields, CONTACT_FILTER_OPTION_RESOURCES, { handleError });

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');

    const {
        contacts,
        loading,
        isInitialLoading,
        pagination,
        filters,
        sorter,
        handleTableChange,
        handleSearch,
        handleFilterChange,
        refetchContacts,
        setSearchString,
        setNormalizedFilters,
        setSorter,
        getHighlightClass
    } = useContactsFetching(channel, handleError);

    const handleEditContact = useCallback((contact) => {
        setSelectedContact(contact);
        setEditModalVisible(true);
    }, []);

    const handleDeleteContact = useCallback((contact) => {
        Modal.confirm({
            title: t('delete_contact'),
            content: t('are_you_sure_delete_contact'),
            okText: t('yes'),
            okType: 'danger',
            cancelText: t('no'),
            onOk: async () => {
                try {
                    await ContactsServices.deleteContact(contact.id);
                    message.success(t('contact_deleted_successfully'));
                    refetchContacts();
                } catch (error) {
                    handleError(error);
                }
            }
        });
    }, [t, refetchContacts, handleError]);

    const handleGoToConversation = useCallback((contact) => {
        navigate(`/messages?contactId=${contact.id}`);
    }, [navigate]);

    const handleEditModalClose = useCallback(() => {
        setEditModalVisible(false);
    }, []);

    const handleSearchInputChange = useCallback((event) => {
        const value = event.target.value;
        setSearchInputValue(value);
        handleSearch(value);
    }, [handleSearch]);

    const handleClearSearch = useCallback(() => {
        setSearchInputValue('');
        handleSearch('');
    }, [handleSearch]);

    const handleSortChange = useCallback((sort) => {
        handleTableChange(
            { ...pagination, current: 1 },
            filters,
            {
                field: sort.sort_by,
                order: sort.sort_direction === 'asc' ? 'ascend' : (sort.sort_direction === 'desc' ? 'descend' : undefined)
            }
        );
    }, [handleTableChange, pagination, filters]);

    const avatarStyle = useMemo(() => ({
        backgroundColor: isDarkMode ? '#1f1f1f' : '#f0f0f0',
        color: '#8c8c8c'
    }), [isDarkMode]);

    const columns = useMemo(() => [
        {
            title: t('contact'),
            key: 'contact',
            render: (_, record) => (
                <Space>
                    <Avatar
                        src={record.picture_url}
                        icon={<UserOutlined />}
                        style={avatarStyle}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text strong>
                            {record.full_name || `${record.first_name || ''} ${record.last_name || ''}`.trim() || t('unknown')}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {record.external_contact_id}
                        </Text>
                    </div>
                </Space>
            ),
        },
        {
            title: t('contact_info'),
            key: 'info',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {record.email && (
                        <Space size={4}>
                            <MailOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
                            <Text>{record.email}</Text>
                        </Space>
                    )}
                    {record.phone && (
                        <Space size={4}>
                            <PhoneOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
                            <Text>{record.phone}</Text>
                        </Space>
                    )}
                    {!record.email && !record.phone && <Text type="secondary">-</Text>}
                </div>
            ),
        },
        {
            title: t('locale'),
            dataIndex: 'locale',
            key: 'locale',
            render: (locale) => locale ? <Text keyboard>{locale}</Text> : '-',
        },
        {
            title: t('created_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => date ? (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(date), 'PPP')}
                </span>
            ) : '-',
            responsive: ['md'],
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 80,
            render: (_, record) => {
                const menuItems = [
                    {
                        key: 'edit',
                        icon: <EditOutlined />,
                        label: t('edit'),
                        onClick: () => handleEditContact(record)
                    },
                    {
                        key: 'conversation',
                        icon: <MessageOutlined />,
                        label: t('go_to_conversation'),
                        onClick: () => handleGoToConversation(record)
                    },
                    {
                        key: 'delete',
                        danger: true,
                        icon: <DeleteOutlined />,
                        label: t('delete'),
                        onClick: () => handleDeleteContact(record)
                    }
                ];

                return (
                    <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                        <Button icon={<MoreOutlined />} size="small" />
                    </Dropdown>
                );
            },
        },
    ], [t, avatarStyle, handleEditContact, handleGoToConversation, handleDeleteContact]);

    return (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Flex
                gap="middle"
                wrap="wrap"
                align="center"
                justify="space-between"
                style={{
                    width: '100%',
                    padding: '8px 0 4px'
                }}
            >
                <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                    <Input
                        placeholder={t('search_contacts')}
                        value={searchInputValue}
                        onChange={handleSearchInputChange}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        allowClear
                        onClear={handleClearSearch}
                        style={{
                            width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                            transition: 'width 0.4s ease-in-out'
                        }}
                        prefix={<SearchOutlined />}
                    />
                </Flex>

                <SharedFilterBar
                    fields={filterFields}
                    value={filters}
                    onChange={handleFilterChange}
                    sortFields={contactSortFields}
                    sortValue={{ sort_by: sorter.field, sort_direction: sorter.order }}
                    onSortChange={handleSortChange}
                />
            </Flex>

            <div style={{ width: '100%' }}>
                <AppTable
                    columns={columns}
                    dataSource={contacts}
                    rowKey="id"
                    scroll={{ x: 800 }}
                    loading={loading || isInitialLoading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: !screens.xs,
                    }}
                    onChange={handleTableChange}
                    rowClassName={(record) => getHighlightClass ? getHighlightClass(record.id) : ''}
                />
            </div>

            <ContactEditModal
                visible={editModalVisible}
                onCancel={handleEditModalClose}
                contact={selectedContact}
                onSuccess={refetchContacts}
            />
        </Space>
    );
};

export default ContactsTab;
